import { http } from './httpClient'
import type {
  AdminAccountOverview,
  AdminMetricsSnapshot,
  AdminRiskRow,
  AdminUserDetail,
  AdminUserSummary,
  Paginated,
  Plan,
} from './types'

export interface AdminApplyRequest {
  reason: string
  idempotency_key: string
  fingerprint: string
}

export interface PlanAttributes {
  name: string
  description: string
  max_projects: number
  max_members: number
  allow_version_history: boolean
  allow_team_members: boolean
  allow_export_import: boolean
  is_available_for_purchase: boolean
  is_active: boolean
}

export const adminApi = {
  risk: (query: Record<string, string | number | undefined>) =>
    http.get<Paginated<AdminRiskRow>>('/api/admin/risk/', { query }),
  account: (workspaceId: number) =>
    http.get<AdminAccountOverview>(`/api/admin/accounts/${workspaceId}/`),
  charges: (workspaceId: number) =>
    http.get<Paginated<Record<string, unknown>>>(`/api/admin/accounts/${workspaceId}/charges/`),
  timeline: (workspaceId: number) =>
    http.get<Paginated<Record<string, unknown>>>(`/api/admin/accounts/${workspaceId}/timeline/`),
  metrics: (query?: Record<string, string | undefined>) =>
    http.get<AdminMetricsSnapshot>('/api/admin/metrics/', { query }),
  plans: () => http.get<Paginated<Plan>>('/api/admin/plans/'),
  planAttributesPreview: (planId: number, attributes: PlanAttributes) =>
    http.post<Record<string, unknown>>(`/api/admin/plans/${planId}/attributes-preview/`, attributes),
  planAttributesApply: (planId: number, attributes: PlanAttributes, apply: AdminApplyRequest) =>
    http.post<Record<string, unknown>>(`/api/admin/plans/${planId}/attributes-apply/`, { ...attributes, ...apply }),
  pricePreview: (planId: number, price_cents: number) =>
    http.post<Record<string, unknown>>(`/api/admin/plans/${planId}/price-preview/`, { price_cents }),
  priceApply: (planId: number, price_cents: number, apply: AdminApplyRequest) =>
    http.post<Record<string, unknown>>(`/api/admin/plans/${planId}/price-apply/`, { price_cents, ...apply }),
  migrationPreview: (planId: number) =>
    http.get<Record<string, unknown>>(`/api/admin/plans/${planId}/migration-preview/`),
  migrationApply: (planId: number, batch_size: number, apply: AdminApplyRequest) =>
    http.post<Record<string, unknown>>(`/api/admin/plans/${planId}/migration-apply/`, { batch_size, ...apply }),
  grantPreview: (workspaceId: number, plan_code: string, days: number) =>
    http.post<Record<string, unknown>>(`/api/admin/accounts/${workspaceId}/grant-preview/`, { plan_code, days }),
  grantApply: (workspaceId: number, plan_code: string, days: number, apply: AdminApplyRequest) =>
    http.post<Record<string, unknown>>(`/api/admin/accounts/${workspaceId}/grant-apply/`, { plan_code, days, ...apply }),
  reconcilePreview: (workspaceId: number) =>
    http.get<Record<string, unknown>>(`/api/admin/accounts/${workspaceId}/reconcile-preview/`),
  reconcileApply: (workspaceId: number, apply: AdminApplyRequest) =>
    http.post<Record<string, unknown>>(`/api/admin/accounts/${workspaceId}/reconcile-apply/`, apply),
  revertPreview: (workspaceId: number) =>
    http.get<Record<string, unknown>>(`/api/admin/accounts/${workspaceId}/revert-preview/`),
  revertApply: (workspaceId: number, apply: AdminApplyRequest) =>
    http.post<Record<string, unknown>>(`/api/admin/accounts/${workspaceId}/revert-apply/`, apply),
  resendVerification: (workspaceId: number, user_id: number) =>
    http.post<{ sent: boolean }>(`/api/admin/accounts/${workspaceId}/resend-verification/`, { user_id }),
  verifyPreview: (workspaceId: number, user_id: number) =>
    http.post<Record<string, unknown>>(`/api/admin/accounts/${workspaceId}/verify-preview/`, { user_id }),
  verifyApply: (workspaceId: number, user_id: number, apply: AdminApplyRequest) =>
    http.post<Record<string, unknown>>(`/api/admin/accounts/${workspaceId}/verify-apply/`, { user_id, ...apply }),
  users: (query?: Record<string, string | number | undefined>) =>
    http.get<Paginated<AdminUserSummary>>('/api/admin/users/', { query }),
  user: (userId: number) => http.get<AdminUserDetail>(`/api/admin/users/${userId}/`),
  sendPasswordReset: (userId: number) =>
    http.post<{ sent: boolean }>(`/api/admin/users/${userId}/send-password-reset/`),
  resendUserVerification: (userId: number) =>
    http.post<{ sent: boolean }>(`/api/admin/users/${userId}/resend-verification/`),
}
